import { Container, IDestroyOptions } from "pixi.js"
import { SoftenedGraphics } from "./softened_graphics"
import type { ShadowPolygon } from "./building_shadow_geometry"

/**
 * Live tuning, like window.occluderFade: window.buildingShadow.enabled / .alpha / .indoors / .softness.
 * `indoors: false` keeps floors under a roof (or under the storey above) lit. Setting
 * window.buildingShadow = false turns the shadows off.
 */
export const BUILDING_SHADOW = { enabled: true, alpha: 0.3, indoors: true, softness: 1.25 }

export const buildingShadowConfig = (): typeof BUILDING_SHADOW => {
    if (typeof window === "undefined") return BUILDING_SHADOW
    const w = window as any
    if (w.buildingShadow === undefined) w.buildingShadow = BUILDING_SHADOW
    const live = w.buildingShadow
    if (live === false) return { ...BUILDING_SHADOW, enabled: false }
    return live && typeof live === "object" ? live : BUILDING_SHADOW
}

/** Above every ground tile layer (zIndex 0), below every sorted sprite and body (100 and up). */
export const BUILDING_SHADOW_Z_INDEX = 1

/** How strongly shadows show at a room's brightness: full by day, gone by night and underground. */
export const daylightFactor = (brightness: number | undefined): number =>
    brightness === undefined ? 1 : Math.min(1, Math.max(0, (brightness - 0.4) / 0.6))

/**
 * Polygons that overlap or nearly touch, grouped, so each building bakes into its own small texture
 * rather than one texture spanning every building in the room.
 */
export const groupPolygons = (polygons: readonly ShadowPolygon[], pad = 8, bucket = 64): ShadowPolygon[][] => {
    const parent = polygons.map((_, i) => i)
    const find = (i: number): number => (parent[i] === i ? i : (parent[i] = find(parent[i])))
    const owner = new Map<string, number>()
    polygons.forEach((polygon, i) => {
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
        for (let k = 0; k < polygon.length; k += 2) {
            minX = Math.min(minX, polygon[k]); maxX = Math.max(maxX, polygon[k])
            minY = Math.min(minY, polygon[k + 1]); maxY = Math.max(maxY, polygon[k + 1])
        }
        for (let bx = Math.floor((minX - pad) / bucket); bx <= Math.floor((maxX + pad) / bucket); bx++) {
            for (let by = Math.floor((minY - pad) / bucket); by <= Math.floor((maxY + pad) / bucket); by++) {
                const key = `${bx},${by}`
                const other = owner.get(key)
                if (other === undefined) owner.set(key, i)
                else parent[find(i)] = find(other)
            }
        }
    })
    const groups = new Map<number, ShadowPolygon[]>()
    polygons.forEach((polygon, i) => {
        const root = find(i)
        const group = groups.get(root)
        if (group) group.push(polygon)
        else groups.set(root, [polygon])
    })
    return [...groups.values()]
}

/**
 * One storey's building shadows: every polygon filled solid, softened and baked once per change
 * (SoftenedGraphics), and shown through the layer's alpha so overlapping shadows never darken twice.
 * No live filter, mask or extra pass per frame.
 */
export class BuildingShadowLayer extends Container {
    private groups: SoftenedGraphics[] = []

    constructor() {
        super()
        this.zIndex = BUILDING_SHADOW_Z_INDEX
        this.interactive = false
        this.interactiveChildren = false
    }

    draw(polygons: readonly ShadowPolygon[], softness: number): void {
        this.clearGroups()
        groupPolygons(polygons).forEach(group => {
            const graphics = new SoftenedGraphics(softness, 2)
            graphics.beginFill(0x000000, 1)
            group.forEach(polygon => graphics.drawPolygon(polygon))
            graphics.endFill()
            this.addChild(graphics)
            graphics.soften()
            this.groups.push(graphics)
        })
    }

    get groupCount(): number { return this.groups.length }

    private clearGroups(): void {
        this.groups.forEach(graphics => {
            graphics.parent?.removeChild(graphics)
            graphics.destroy()
        })
        this.groups = []
    }

    destroy(options?: IDestroyOptions | boolean): void {
        this.clearGroups()
        super.destroy(options)
    }
}
